import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const Skills: React.FC = () => {
  // SEO keywords based on your skills
  const seoKeywords = [
    'Full Stack Developer',
    'Software Engineer',
    'ASP.NET Core',
    'C#',
    'Java',
    'TypeScript',
    'React',
    'Angular',
    'Azure',
    'AWS',
    'Cloud Computing',
    'DevOps',
    'SQL',
    'Solution Architecture',
    'Team Leadership'
  ].join(', ');
  const skillCategories = [
    {
      title: 'Core Enterprise Technologies',
      description: 'Enterprise-grade development expertise with proven track record',
      skills: [
        { 
          name: '.NET / ASP.NET Core', 
          expertise: '13+ years',
          description: 'Led multiple enterprise-scale applications, microservices architecture'
        },
        { 
          name: 'C#', 
          expertise: '13+ years',
          description: 'Enterprise software development, high-performance applications'
        },
        { 
          name: 'Java', 
          expertise: '1+ years',
          description: 'Spring Boot, enterprise applications, RESTful services'
        },
        { 
          name: 'TypeScript & JavaScript', 
          expertise: '6+ years',
          description: 'Modern web applications, enterprise-grade frontends'
        },
        { 
          name: 'React & Angular', 
          expertise: '6+ years',
          description: 'Large-scale SPA development, component libraries'
        },
        { 
          name: 'Modern Web Frameworks', 
          expertise: '2+ years',
          description: 'Svelte, performance optimization'
        },
      ]
    },
    {
      title: 'Cloud & DevOps Excellence',
      description: 'Extensive experience in cloud architecture and automation',
      skills: [
        { 
          name: 'Azure Cloud Platform', 
          expertise: '6+ years',
          description: 'Azure Key Vault, Azure Functions, Azure Container Services, Azure Event Queues'
        },
        { 
          name: 'AWS Services', 
          expertise: '4+ years',
          description: 'Amazon SNS, Amazon SQS, AWS Lambda, Amazon RDS, IAM, Amazon CDK'
        },
        { 
          name: 'DevOps & CI/CD', 
          expertise: '7+ years',
          description: 'Azure DevOps, Jenkins, Docker, Octopus Deploy, automated testing, continuous deployment'
        }
      ]
    },
    {
      title: 'Data & Integration Solutions',
      description: 'Expertise in enterprise data management and system integration',
      skills: [
        { 
          name: 'SQL & Database Systems', 
          expertise: '8+ years',
          description: 'SQL Server, PostgreSQL, performance optimization, data modeling'
        },
        { 
          name: 'Event-Driven Architecture', 
          expertise: '5+ years',
          description: 'RabbitMQ, Amazon SQS, distributed systems'
        },
        { 
          name: 'API Design', 
          expertise: '7+ years',
          description: 'RESTful APIs, GraphQL, API security, documentation'
        },
        { 
          name: 'Enterprise Integration', 
          expertise: '5+ years',
          description: 'System integration, middleware solutions, EJBCA'
        },
      ]
    },
    {
      title: 'Leadership & Architecture',
      description: 'Technical leadership and enterprise architecture expertise',
      skills: [
        { 
          name: 'Solution Architecture', 
          expertise: '6+ years',
          description: 'Enterprise architecture, scalable solutions, technical roadmaps'
        },
        { 
          name: 'Team Leadership', 
          expertise: '4+ years',
          description: 'Technical team lead, mentoring, code reviews, best practices'
        },
        { 
          name: 'Agile Development', 
          expertise: '7+ years',
          description: 'Scrum master certified, sprint planning, agile transformation'
        },
        { 
          name: 'Quality & Testing', 
          expertise: '6+ years',
          description: 'Test automation, CI/CD integration, quality metrics'
        } 
      ]
    }
  ];

  return (
    <section id="skills" className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <meta name="description" content="Experienced software engineer with expertise in .NET, Java, Cloud Computing, and modern web technologies. Skilled in React, Angular, Azure, and AWS." />
        <meta name="keywords" content={seoKeywords} />
        <meta property="og:title" content="Skills & Expertise - Software Engineer Portfolio" />
        <meta property="og:description" content="Full-stack developer with expertise in enterprise software development, cloud solutions, and team leadership." />
        <meta property="og:type" content="profile" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "Person",
            "name": "Balaji Iyer",
            "jobTitle": "Software Engineer",
            "description": "Full-stack developer with expertise in enterprise software development",
            "knowsAbout": skillCategories.map(category => 
              category.skills.map(skill => skill.name)
            ).flat(),
            "url": window.location.href
          })}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Professional Expertise</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Experienced software architect and technical leader with a proven track record in delivering enterprise-scale solutions.
            Specialized in cloud-native applications, distributed systems, and digital transformation.
          </p>
        </motion.div>

        <div className="space-y-16">
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">{category.title}</h3>
                <p className="text-gray-600">{category.description}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.skills.map((skill, skillIndex) => (
                  <motion.div
                    key={skill.name}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: skillIndex * 0.05 }}
                    viewport={{ once: true }}
                    whileHover={{ 
                      scale: 1.02,
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                    }}
                    className="relative overflow-hidden rounded-xl p-6 bg-white border border-gray-200 shadow-sm"
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">{skill.name}</h4>
                        <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {skill.expertise}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">{skill.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <h3 className="text-2xl font-semibold text-gray-900 mb-8">Additional Technologies</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'JWT', 'OKTA', 'Docker', 'Linux', 'REST APIs', 'OAuth/OIDC',
              'Serverless', 'Git'
            ].map((skill, index) => (
              <motion.div
                key={skill}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="
                  px-5 py-2.5 rounded-lg text-sm font-medium
                  bg-gradient-to-r from-gray-50 to-gray-100
                  text-gray-700 border border-gray-200
                  hover:border-gray-300 hover:shadow-md
                  transition-all duration-200
                "
              >
                {skill}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;